import * as ents from '@attain-apps/entities'

import type * as models from './models'

/**
 * Route-bound scenario dropdown state for the active portal app.
 */
export interface State {
  /**
   * Latest scenario list read for this route-bound app.
   *
   * This is intentionally a small client-facing read cache, not a second
   * source of truth. `me` still owns the login/navigation snapshot, but
   * dropdown refreshes should not write into `me` because that would wake the
   * whole app context graph and can cause app-level entity collections to
   * reload while the user is merely opening the dropdown.
   *
   * `undefined` means this route-bound feature has not refreshed yet. Selectors
   * can then fall back to `me.contexts.selectors.availableScenarios` so the
   * first dropdown render is instant.
   */
  scenarios?: readonly ents.core.admin.Scenario[]
  /**
   * Indicates the dropdown-owned scenario list is being refreshed.
   *
   * This flag must stay local to the dropdown feature. It should not drive the
   * global loading/ripple state because a background list refresh is not a page
   * navigation or app context reload.
   */
  isRefreshingScenarios?: boolean
  /**
   * Last failed dropdown-list refresh error.
   *
   * We keep this local so the UI can choose between showing stale scenarios,
   * showing a small local error, or doing nothing. Refresh failure should not
   * invalidate the root `me` snapshot.
   */
  refreshScenariosError?: unknown
  /**
   * Indicates whether the scenario dropdown overlay is currently open.
   */
  presentOverlay?: boolean
  /**
   * Expanded/collapsed state for each scenario visibility section.
   */
  sectionVisibilityLookup: Record<models.Section.Id, boolean>
  /**
   * Draft state for the create/update scenario dialog.
   */
  editDialog?: State.EditDialog
  /**
   * Scenario currently awaiting delete confirmation.
   */
  deleteDialog?: State.DeleteDialog
  /**
   * Scenarios with an in-flight visibility move.
   */
  movingScenarioIds: Record<ents.core.admin.Scenario.Id, boolean>
  /**
   * Last failed visibility move error.
   */
  moveError?: unknown
}
export namespace State {
  export const CREATE_DRAFT_SCENARIO_ID = '__route-bound-scenario-draft__'

  export interface EditDialog {
    readonly mode: EditDialog.Mode
    readonly scenarioId?: ents.core.admin.Scenario.Id
    readonly draft: ents.core.admin.Scenario.Input
    readonly isSubmitting?: boolean
    readonly error?: unknown
  }
  export namespace EditDialog {
    export type Mode = 'create' | 'update'
  }

  export interface DeleteDialog {
    readonly scenarioId: ents.core.admin.Scenario.Id
    readonly isSubmitting?: boolean
    readonly error?: unknown
  }

  export const INITIAL: State = {
    movingScenarioIds: {},
    sectionVisibilityLookup: {
      [ents.core.admin.Scenario.Visibility.Pinned]: true,
      [ents.core.admin.Scenario.Visibility.Visible]: true,
      [ents.core.admin.Scenario.Visibility.Hidden]: true,
    },
  }

  export function startRefreshingScenarios(state: State): State {
    return {
      ...state,
      isRefreshingScenarios: true,
      refreshScenariosError: undefined,
    }
  }

  export function finishRefreshingScenarios(
    state: State,
    scenarios: readonly ents.core.admin.Scenario[],
  ): State {
    return {
      ...state,
      isRefreshingScenarios: false,
      refreshScenariosError: undefined,
      scenarios,
    }
  }

  export function failRefreshingScenarios(
    state: State,
    error: unknown,
  ): State {
    return {
      ...state,
      isRefreshingScenarios: false,
      refreshScenariosError: error,
    }
  }

  export function cancelRefreshingScenarios(state: State): State {
    return {
      ...state,
      isRefreshingScenarios: false,
    }
  }

  export function openCreate(state: State): State {
    return {
      ...state,
      editDialog: {
        draft: ents.core.admin.Scenario.Input.generate(),
        mode: 'create',
      },
      presentOverlay: false,
    }
  }

  export function openEdit(
    state: State,
    scenario: ents.core.admin.Scenario,
  ): State {
    return {
      ...state,
      editDialog: {
        draft: getInputFromScenario(scenario),
        mode: 'update',
        scenarioId: scenario.id,
      },
      presentOverlay: false,
    }
  }

  export function editDraft(
    state: State,
    updates: Partial<ents.core.admin.Scenario.Input>,
  ): State {
    if (!state.editDialog) {
      return state
    }

    return {
      ...state,
      editDialog: {
        ...state.editDialog,
        draft: {
          ...state.editDialog.draft,
          ...updates,
        },
      },
    }
  }

  export function cancelEdit(state: State): State {
    return {
      ...state,
      editDialog: undefined,
    }
  }

  export function startSubmittingEdit(state: State): State {
    if (!state.editDialog) {
      return state
    }

    return {
      ...state,
      editDialog: {
        ...state.editDialog,
        error: undefined,
        isSubmitting: true,
      },
    }
  }

  export function finishSubmittingEdit(state: State): State {
    return {
      ...state,
      editDialog: undefined,
      presentOverlay: false,
    }
  }

  export function failSubmittingEdit(state: State, error: unknown): State {
    if (!state.editDialog) {
      return state
    }

    return {
      ...state,
      editDialog: {
        ...state.editDialog,
        error,
        isSubmitting: false,
      },
    }
  }

  export function cancelSubmittingEdit(state: State): State {
    if (!state.editDialog) {
      return state
    }

    return {
      ...state,
      editDialog: {
        ...state.editDialog,
        isSubmitting: false,
      },
    }
  }

  export function openDelete(
    state: State,
    scenario: ents.core.admin.Scenario,
  ): State {
    return {
      ...state,
      deleteDialog: {
        scenarioId: scenario.id,
      },
      presentOverlay: false,
    }
  }

  export function cancelDelete(state: State): State {
    return {
      ...state,
      deleteDialog: undefined,
    }
  }

  export function startSubmittingDelete(state: State): State {
    if (!state.deleteDialog) {
      return state
    }

    return {
      ...state,
      deleteDialog: {
        ...state.deleteDialog,
        error: undefined,
        isSubmitting: true,
      },
    }
  }

  export function finishSubmittingDelete(state: State): State {
    return {
      ...state,
      deleteDialog: undefined,
      presentOverlay: false,
    }
  }

  export function failSubmittingDelete(state: State, error: unknown): State {
    if (!state.deleteDialog) {
      return state
    }

    return {
      ...state,
      deleteDialog: {
        ...state.deleteDialog,
        error,
        isSubmitting: false,
      },
    }
  }

  export function cancelSubmittingDelete(state: State): State {
    if (!state.deleteDialog) {
      return state
    }

    return {
      ...state,
      deleteDialog: {
        ...state.deleteDialog,
        isSubmitting: false,
      },
    }
  }

  export function startMovingScenario(
    state: State,
    scenarioId: ents.core.admin.Scenario.Id,
  ): State {
    return {
      ...state,
      moveError: undefined,
      movingScenarioIds: {
        ...state.movingScenarioIds,
        [scenarioId]: true,
      },
    }
  }

  export function finishMovingScenario(
    state: State,
    scenarioId: ents.core.admin.Scenario.Id,
  ): State {
    const { [scenarioId]: _, ...movingScenarioIds } = state.movingScenarioIds

    return {
      ...state,
      movingScenarioIds,
    }
  }

  export function failMovingScenario(
    state: State,
    scenarioId: ents.core.admin.Scenario.Id,
    error: unknown,
  ): State {
    return {
      ...finishMovingScenario(state, scenarioId),
      moveError: error,
    }
  }

  function getInputFromScenario(
    scenario: ents.core.admin.Scenario,
  ): ents.core.admin.Scenario.Input {
    return {
      alias: scenario.alias,
      description: scenario.description,
      name: scenario.name,
      period: scenario.period,
      variables: scenario.variables ?? {},
      visibility: scenario.visibility,
    }
  }
}
